'use client';

import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { ArrowRight, Leaf, Sparkles, TrendingUp, Utensils } from 'lucide-react';
import NextLink from 'next/link';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { PlatformStats } from '@/features/landing/actions/getPlatformStatsAction';
import { HeroVideoPlayer } from '@/features/landing/components/hero-video-player';
import { useLandingCTATracking } from '@/features/landing/hooks/useLandingCTATracking';
import { useSectionVisibility } from '@/features/landing/hooks/useSectionVisibility';
import { Link } from '@/i18n/routing';
import { siteConfig } from '@/lib/config/site';
import { useFormatting } from '@/lib/formatting';

// Animation variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 100,
      damping: 15,
    },
  },
};

const floatingCardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 80,
      damping: 12,
      delay: 0.8,
    },
  },
};

// Animated number component for stats
function AnimatedNumber({ value, suffix = '' }: { value: number; suffix?: string }) {
  const shouldReduceMotion = useReducedMotion();
  const spring = useSpring(0, {
    stiffness: 50,
    damping: 20,
    restDelta: 0.001,
  });

  useEffect(() => {
    spring.set(shouldReduceMotion ? value : value);
  }, [spring, value, shouldReduceMotion]);

  const display = useTransform(spring, (current) => {
    if (current >= 1000) {
      return `${(current / 1000).toFixed(1)}k${suffix}`;
    }
    return `${Math.round(current)}${suffix}`;
  });

  if (shouldReduceMotion) {
    return (
      <span>
        {value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
        {suffix}
      </span>
    );
  }

  return <motion.span>{display}</motion.span>;
}

interface HeroSectionProps {
  stats?: PlatformStats;
}

export function HeroSection({ stats }: HeroSectionProps) {
  const t = useTranslations('landing.hero');
  const containerRef = useRef<HTMLElement>(null);
  const { trackCTAClick } = useLandingCTATracking();
  const { formatWeight } = useFormatting();
  const { ref: visibilityRef } = useSectionVisibility('hero');
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 50]);

  const handlePrimaryCTAClick = () => {
    trackCTAClick({
      ctaLocation: 'hero_primary',
      ctaText: t('cta.pilot'),
      ctaType: 'primary',
      section: 'hero',
      destinationUrl: siteConfig.bookingUrl,
    });
  };

  return (
    <section
      id="home"
      ref={(el) => {
        // Combine refs for both scroll tracking and section visibility
        (containerRef as React.MutableRefObject<HTMLElement | null>).current = el;
        (visibilityRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
      className="relative w-full py-12 lg:py-16 overflow-hidden bg-background border-b border-border"
    >
      <div className="container relative z-10 mx-auto px-4 md:px-6 lg:px-8 max-w-[1440px]">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12 w-full">
          {/* Left Column: Text Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-5 sm:space-y-6 text-center lg:text-left"
          >
            {/* Animated Badge */}
            <motion.div variants={itemVariants} className="flex justify-center lg:justify-start">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
              >
                <Badge
                  variant="outline"
                  className="px-4 py-1.5 text-sm border-border bg-card/50 text-foreground backdrop-blur-sm shadow-sm cursor-default"
                >
                  <motion.span
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    className="inline-block"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-2 text-primary" />
                  </motion.span>
                  {t('badge')}
                </Badge>
              </motion.div>
            </motion.div>

            {/* Heading */}
            <motion.h1
              variants={itemVariants}
              className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl leading-tight text-balance px-2 sm:px-0"
            >
              {t.rich('title', {
                highlight: (chunks) => <span className="text-primary inline-block">{chunks}</span>,
                danger: (chunks) => <span className="text-red-500/70 inline-block">{chunks}</span>,
              })}
            </motion.h1>

            {/* Description */}
            <motion.p
              variants={itemVariants}
              className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto lg:mx-0 text-balance px-2 sm:px-0"
            >
              {t.rich('description', {
                highlight: (chunks) => <span className="text-primary font-medium">{chunks}</span>,
                danger: (chunks) => <span className="text-red-400/60 font-normal">{chunks}</span>,
              })}
            </motion.p>

            {/* CTA Button */}
            <motion.div
              variants={itemVariants}
              className="flex items-center justify-center lg:justify-start pt-2 sm:pt-4"
            >
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                className="relative group"
              >
                {/* Subtle glow effect behind button */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-emerald-400 rounded-xl blur-md opacity-40 group-hover:opacity-70 transition-opacity duration-500" />

                <Button
                  asChild
                  size="lg"
                  className="relative h-12 sm:h-14 px-6 sm:px-10 text-base sm:text-lg font-semibold cursor-pointer w-full sm:w-auto max-w-xs sm:max-w-none bg-gradient-to-r from-primary via-emerald-500 to-primary bg-[length:200%_100%] hover:bg-[length:100%_100%] transition-[background-size] duration-500 text-white border-0 shadow-2xl shadow-primary/40 overflow-hidden"
                >
                  <NextLink
                    href={siteConfig.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handlePrimaryCTAClick}
                    className="relative z-10 flex items-center gap-2"
                  >
                    {/* Shimmer effect - hidden on hover */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 group-hover:opacity-0 transition-opacity duration-200"
                      animate={{
                        x: ['-200%', '200%'],
                      }}
                      transition={{
                        duration: 0.7,
                        repeat: Infinity,
                        repeatDelay: 2.5,
                        ease: 'easeInOut',
                      }}
                    />
                    <span className="relative z-10">{t('cta.pilot')}</span>
                    {/* Animated arrow - hidden on hover */}
                    <motion.span
                      animate={{ x: [0, 6, -1, 0] }}
                      transition={{
                        duration: 0.5,
                        repeat: Infinity,
                        repeatDelay: 2.5,
                        times: [0, 0.4, 0.7, 1],
                        ease: [0.25, 0.46, 0.45, 0.94],
                      }}
                      className="inline-block relative z-10 group-hover:opacity-0 transition-opacity duration-150"
                    >
                      <ArrowRight className="h-5 w-5" />
                    </motion.span>
                    {/* Static arrow - visible on hover, more prominent */}
                    <span className="absolute right-6 sm:right-10 z-10 opacity-0 group-hover:opacity-100 translate-x-0 group-hover:translate-x-2 scale-100 group-hover:scale-125 transition-all duration-200">
                      <ArrowRight className="h-5 w-5" />
                    </span>
                  </NextLink>
                </Button>
              </motion.div>
            </motion.div>

            {/* Social Proof / Crystal Balls */}
            <motion.div
              variants={itemVariants}
              className="pt-4 sm:pt-5 border-t border-border/50 mt-4 sm:mt-5"
            >
              <div className="flex items-center gap-3 sm:gap-6 flex-wrap justify-center lg:justify-start">
                <div className="flex items-center -space-x-2 sm:-space-x-3">
                  {/* Active Slot - Reduced visual prominence */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.6 }}
                    className="relative z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-red-500/10 to-orange-900/10 border border-red-500/20 flex items-center justify-center backdrop-blur-sm"
                  >
                    <span className="text-sm sm:text-base filter drop-shadow-sm">🔮</span>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.9, type: 'spring', stiffness: 500 }}
                      className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500/80 rounded-full border border-background flex items-center justify-center"
                    >
                      <svg
                        className="w-1.5 h-1.5 sm:w-2 sm:h-2 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </motion.div>
                  </motion.div>

                  {/* Empty Slots - Easter Egg Links to Pilot Section */}
                  {[2, 3, 4, 5].map((i, index) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 15,
                        delay: 0.7 + index * 0.1,
                      }}
                      whileHover={{ scale: 1.15, zIndex: 20 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link
                        href="/#pilot"
                        onClick={(e) => {
                          e.preventDefault();
                          const pilotSection = document.querySelector('#pilot');
                          if (pilotSection) {
                            pilotSection.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start',
                            });
                          }
                        }}
                        className="group w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-dashed border-border bg-card/30 flex items-center justify-center transition-colors hover:border-primary hover:bg-primary/10 cursor-pointer"
                      >
                        <span className="text-sm sm:text-base opacity-20 grayscale group-hover:opacity-60 group-hover:grayscale-0 transition-all">
                          🔮
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                <div className="flex flex-col text-left">
                  <span className="text-sm font-medium text-foreground">
                    {t('socialProof.fired')}
                  </span>
                  <span className="text-xs text-muted-foreground">{t('socialProof.ready')}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="flex items-center justify-end h-full flex-col gap-4">
            {/* Right Column: Video Interface */}
            <motion.div
              style={{ y }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative mx-auto w-full max-w-[600px] perspective-1000 px-2 sm:px-0"
            >
              <div className="relative rounded-2xl bg-muted border border-border shadow-2xl overflow-hidden group">
                {/* Browser/App Header */}
                <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-border bg-card">
                  <div className="flex space-x-1.5 sm:space-x-2">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#FF5F56] border border-[#E0443E]" />
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#FFBD2E] border border-[#DEA123]" />
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#27C93F] border border-[#1AAB29]" />
                  </div>
                  <div className="px-3 sm:px-4 py-0.5 sm:py-1 rounded-md bg-background border border-border text-[9px] sm:text-[10px] font-mono text-muted-foreground shadow-inner">
                    bite-club.app
                  </div>
                  {/* Spacer to balance traffic lights */}
                  <div className="w-[38px] sm:w-[50px]" />
                </div>

                {/* Video Content - aspect ratio matches video dimensions 1958x1100 */}
                <div className="relative aspect-[1958/1100] bg-black cursor-pointer overflow-hidden">
                  {/* Hero Video Player */}
                  <HeroVideoPlayer
                    previewSrc="/landing/video/LandingVideo_Preview.mp4"
                    fullVideoSrc="/landing/video/LandingVideo_with_audio.mp4"
                    posterSrc="/landing/user-order.png"
                  />

                  {/* Floating Stat Card Animation */}
                  <motion.div
                    variants={floatingCardVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className="absolute right-2 sm:right-6 top-6 sm:top-8 bg-card/90 backdrop-blur border border-border p-3 sm:p-4 rounded-xl shadow-2xl max-w-[140px] sm:max-w-[160px] cursor-default"
                  >
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-primary"
                      />
                      <span className="text-[10px] sm:text-xs text-muted-foreground font-medium">
                        Waste Reduced
                      </span>
                    </div>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.2, type: 'spring', stiffness: 200 }}
                      className="text-xl sm:text-2xl font-bold text-foreground tracking-tight"
                    >
                      -40%
                    </motion.div>
                    <div className="h-1 sm:h-1.5 w-full bg-muted rounded-full mt-2 sm:mt-3 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: '40%' }}
                        transition={{ delay: 1.4, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="h-full bg-primary rounded-full"
                      />
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
            {/* Platform Stats - Below Video */}
            {stats && stats.totalMealsOrdered > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-4 flex flex-col items-center gap-2"
              >
                {/* Early Pilot Results Badge */}
                <Badge
                  variant="outline"
                  className="px-4 py-1.5 text-sm border-border/50 bg-muted/30 text-muted-foreground"
                >
                  {t('stats.pilotBadge')}
                </Badge>

                <div className="flex items-center justify-center gap-6 sm:gap-8">
                  {/* Orders Count */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                    className="text-center"
                  >
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <motion.div
                        initial={{ rotate: -30, opacity: 0 }}
                        whileInView={{ rotate: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3, type: 'spring' }}
                      >
                        <Utensils className="w-4 h-4 text-primary" />
                      </motion.div>
                      <span className="text-xl sm:text-2xl font-bold text-foreground tabular-nums">
                        <AnimatedNumber value={stats.totalMealsOrdered} suffix="+" />
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      {t('stats.orders')}
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="w-px h-10 bg-border/50"
                  />

                  {/* Waste Saved */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                    className="text-center"
                  >
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <motion.div
                        initial={{ rotate: -30, opacity: 0 }}
                        whileInView={{ rotate: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5, type: 'spring' }}
                      >
                        <Leaf className="w-4 h-4 text-primary" />
                      </motion.div>
                      <span className="text-xl sm:text-2xl font-bold text-foreground tabular-nums">
                        {formatWeight(stats.wasteSavedKg)}
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      {t('stats.wasteSaved')}
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 }}
                    className="w-px h-10 bg-border/50"
                  />

                  {/* Money Saved */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                    className="text-center"
                  >
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <motion.div
                        initial={{ rotate: -30, opacity: 0 }}
                        whileInView={{ rotate: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.7, type: 'spring' }}
                      >
                        <TrendingUp className="w-4 h-4 text-primary" />
                      </motion.div>
                      <span className="text-xl sm:text-2xl font-bold text-foreground tabular-nums">
                        <AnimatedNumber value={stats.moneySavedEuros} suffix="€" />
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      {t('stats.moneySaved')}
                    </span>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
